import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { ErrorType } from "@/types";
import { PriceMaster, ItemType } from "@/types/system-setting/priceMaster";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import PriceMasterForm from "../modal/form";
import {
  useGetPriceMastersQuery,
  useDeletePriceMasterMutation,
} from "@/services/priceMasterApi";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { DataTableRowActions } from "./data-table-row-actions";
import { displayValue } from "@/lib/helper";
import TableFilters from "@/components/ui/table/data-table-filter";
import { useGetBusinessUnitsQuery } from "@/services/businessUnitApi";

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
}

export default function PriceMasterTableView() {
  const adminAccess = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).system_configuration ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);

  const {
    data: priceMasterData,
    isLoading,
    refetch,
  } = useGetPriceMastersQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );

  // Already limited by the API to the business units this staff member is assigned to.
  const { data: businessUnitsData } = useGetBusinessUnitsQuery(
    { org_id: Number(orgId), limit: 100 },
    { skip: !orgId }
  );
  const businessUnits = Array.isArray(businessUnitsData?.data) ? businessUnitsData.data : [];

  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});
  const filterDisplay = [
    {
      type: "select",
      name: "business_unit_id",
      label: "Business Unit",
      options: businessUnits.map((bu) => ({ id: String(bu.id), name: bu.name })),
      function: (value: string) =>
        setFilterData((prev) => ({ ...prev, business_unit_id: value })),
    },
  ];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [data, setData] = useState<PriceMaster | undefined>(undefined);

  const tableData = React.useMemo(() => {
    return Array.isArray(priceMasterData?.data) ? priceMasterData.data : [];
  }, [priceMasterData]);

  const { toast } = useToast();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const handleEdit = (data: PriceMaster) => {
    setData(data);
    setAction("edit");
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<PriceMaster> = {
    id: "actions",
    header: "Actions",
    maxSize: 100,
    cell: ({ row }) => (
      <DataTableRowActions
        access={adminAccess}
        data={row.original}
        refetch={refetch}
        handleEdit={handleEdit}
      />
    ),
  };

  const columns: ColumnDef<PriceMaster>[] = [
    {
      accessorKey: "rule_code",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Code No"
          sortKey="rule_code"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => <span>{displayValue(row.original.rule_code)}</span>,
    },
    {
      accessorKey: "rule_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="rule_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => <span>{displayValue(row.original.rule_name)}</span>,
    },
    {
      accessorKey: "item_type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Item Type"
          sortKey="item_type"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => <span className="capitalize">{row.original.item_type?.replace("_", " ")}</span>,
    },
    {
      accessorKey: "business_unit_name",
      header: "Business Unit",
      cell: ({ row }) => <span>{displayValue(row.original.business_unit_name)}</span>,
    },
    ...(adminAccess !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
    setData(undefined);
  };

  const totalRecords = priceMasterData?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        <div className="w-full lg:flex-1 min-w-0">
          <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />
        </div>

        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          {adminAccess !== "read" && (
            <Button
              className="bg-primary text-sm text-black flex items-center justify-center gap-1 h-8 px-2 w-9 sm:w-auto"
              onClick={handleOpen}
            >
              <PlusIcon className="size-4 shrink-0" />
              <span className="hidden sm:inline">Create New</span>
            </Button>
          )}
          {businessUnits.length > 1 && (
            <button
              type="button"
              aria-label="Filter price masters"
              className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
              onClick={() => setOpenFilter(true)}
            >
              <i className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}></i>
            </button>
          )}
        </div>
      </div>
      <div className="hidden md:block rounded-none border border-border">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]">
            <TableHeader className="bg-secondary">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => {
                      if (adminAccess !== "read") {
                        handleEdit(row.original);
                      }
                    }}
                    className={adminAccess !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
       </ScrollArea>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center py-10">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : tableData.length ? (
          tableData.map((row: PriceMaster) => (
            <div
              key={row.id}
              className="border border-border rounded-md p-3 space-y-2 bg-white"
              onClick={() => {
                if (adminAccess !== "read") {
                  handleEdit(row);
                }
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">
                    {displayValue(row.rule_name)}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {displayValue(row.rule_code)}
                  </p>
                </div>
                <div
                  className="shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {adminAccess !== "read" && (
                    <DataTableRowActions
                      access={adminAccess}
                      data={row}
                      refetch={refetch}
                      handleEdit={handleEdit}
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <div className="text-gray-500">Item Type</div>
                <div className="text-right capitalize">{row.item_type?.replace("_", " ")}</div>
                <div className="text-gray-500">Business Unit</div>
                <div className="text-right">{displayValue(row.business_unit_name)}</div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">
            No records found.
          </div>
        )}
      </div>

      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <PriceMasterForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        data={data}
        setData={setData}
        refetch={refetch}
        action={action}
        setAction={setAction}
      />
    </div>
  );
}
